/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form } from 'react-bootstrap';

import SpinnerButton from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import { FormSelect } from '../../../app/components/FormSelect';
import {
  CreateUpdateSupportingExaminationDictDto,
  CreateUpdateSupportingExaminationDictDtoTypeEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateSupportingExaminationDictDto';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { Selector } from '../Selects/Selector';
import MultiSelectSortable, {
  ReactSelectOption,
} from '../Selects/SortableSelect';

interface SupportingExaminationDictEditorProps {
  id?: string;
  supportingExamDict: CreateUpdateSupportingExaminationDictDto;
  handleClose?: () => void;
  show?: boolean;
  allTests: ReactSelectOption[];
  onCompleted: (
    supportingExamDict: CreateUpdateSupportingExaminationDictDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const SupportingExaminationDictEditor: React.FC<
  SupportingExaminationDictEditorProps
> = props => {
  const [supportingExamDict, setSupportingExamDict] = useState(
    props.supportingExamDict,
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [testName, setTestName] = useState<string>('');
  const spd = useSpdCore();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setSupportingExamDict(props.supportingExamDict);
    }
  }, [props.show, props.supportingExamDict, props.id]);

  const createSupportingExamDict = async () => {
    setIsSaving(true);
    props
      .onCompleted(supportingExamDict, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
      })
      .catch(error => {
        setSaveErrors(error);
        setShowSaveErrors(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const propertyChange = (
    obj: Partial<CreateUpdateSupportingExaminationDictDto>,
  ) => {
    setSupportingExamDict({ ...supportingExamDict, ...obj });
  };

  const textOrEmpty = (text: string | null | undefined) => text || '';

  const getAlternativeTestsOptions = (): ReactSelectOption[] => {
    return (
      supportingExamDict?.alternativeTests?.map(testId => {
        const test = props.allTests.find(t => t.value === testId);
        return test || { value: testId, label: testId };
      }) || []
    );
  };

  const provideTests = async (text: string) => {
    return props.allTests.filter(
      (test: ReactSelectOption) =>
        !text || test.label.toLowerCase().includes(text.toLowerCase()),
    );
  };

  useEffect(() => {
    let isMounted = true;
    if (supportingExamDict?.testId) {
      spd.tests
        .getTest(supportingExamDict.testId)
        .then(test => {
          if (isMounted) {
            setTestName(textOrEmpty(test?.name));
          }
        })
        .catch(() => {
          if (isMounted) {
            setTestName('');
          }
        });
    } else {
      setTestName('');
    }
    return () => {
      isMounted = false;
    };
  }, [supportingExamDict?.testId, spd.tests]);

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? 'Edycja rodzajów badań pomocniczych'
              : 'Dodawanie nowych rodzajów badań pomocniczych'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-2">
            <Card.Header>Rodzaj badania pomocniczego</Card.Header>
            <Card.Body>
              <Form className="d-grid gap-3">
                <FormRow controlId="name" label="Nazwa">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={supportingExamDict?.name || ''}
                    onChange={e => propertyChange({ name: e.target.value })}
                  />
                </FormRow>
                <FormRow controlId="mode" label="Typ">
                  <FormSelect
                    value={supportingExamDict?.type}
                    options={[
                      { value: 'helpExam', label: 'Badanie pomocnicze' },
                      {
                        value: 'specialisedConsultation',
                        label: 'Konsultacja specjalistyczna',
                      },
                    ]}
                    onChange={value =>
                      propertyChange({
                        type: value as CreateUpdateSupportingExaminationDictDtoTypeEnum,
                      })
                    }
                  />
                </FormRow>
                <FormRow controlId="test" label="Test">
                  <Selector
                    value={supportingExamDict?.testId ? testName : ''}
                    onChange={chosen =>
                      propertyChange({ testId: chosen.value })
                    }
                    provider={provideTests}
                    clearable={true}
                  />
                </FormRow>
                <FormRow
                  controlId="alternativeTests"
                  label="Testy alternatywne"
                >
                  <MultiSelectSortable
                    options={props.allTests}
                    defaultValue={getAlternativeTestsOptions()}
                    onChange={options =>
                      propertyChange({
                        alternativeTests: (options || []).map(o => o.value),
                      })
                    }
                  />
                </FormRow>
                <FormRow controlId="synevoCode" label="Synevo">
                  <Form.Control
                    value={supportingExamDict?.synevoCode || ''}
                    onChange={e =>
                      propertyChange({ synevoCode: e.target.value })
                    }
                  />
                </FormRow>
                <FormRow controlId="user_active" label="Aktywny">
                  <Form.Check
                    type="checkbox"
                    checked={supportingExamDict?.active ? true : false}
                    onChange={e => propertyChange({ active: e.target.checked })}
                  />
                </FormRow>
              </Form>
            </Card.Body>
          </Card>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          <SpinnerButton
            variant="outline-primary"
            isLoading={isSaving}
            onClick={createSupportingExamDict}
          >
            Zapisz
          </SpinnerButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};
